import { computed, onUnmounted, ref } from 'vue';
import type { MessageApi } from 'naive-ui';
import type {
  BlendPointRef,
  ChamferEditSessionPayload,
  ChamferFeatureDefinition,
  FeatureDefinition,
  FeaturePrototype,
  FilletEditSessionPayload,
  FilletFeatureDefinition,
  SelectionItemRef,
} from '@puppycad/cad-core';
import {
  type BlendPreviewGuide,
  BlendFeatureSelectionSlotResolver,
  normalizeOffsetFaceChamferDefinition,
} from '@puppycad/cad-core';
import type { ExtrudeViewportRef, ViewportSelectionSnapshot } from '../extrude-types';
import {
  canPreviewChamfer,
  canPreviewFillet,
  canSubmitChamfer,
  canSubmitFillet,
  clonePlainData,
  describeBlendSelectionTarget,
  normalizeChamferDefinitionForFaceFaceMode,
  normalizeFilletDefinitionForTypeSwitch,
} from '@/core/features/blend/command-utils';
import {
  createDefaultChamferDefinition,
  createDefaultFilletDefinition,
  normalizeConstantFilletDefinition,
} from '@/core/features/blend/defaults';
import {
  clampVariableRadiusInstanceCount,
  DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
  ensureVariableRadiusEndpointPoints,
  resolveSelectedVariableRadiusPointKey,
} from '@/core/features/blend/variable-radius';
import { mergeDisplayItemsFromSelectionRefs } from '@/core/features/blend/display';
import {
  buildBlendPartialReferencePointFromObject,
  buildBlendPointRefFromObject,
  buildBlendSelectionFromObject,
  labelBlendSelectionItemByTopologyIndex,
} from '@/core/features/blend/selection';
import {
  ensurePartialEdgeParams,
  updatePartialEdgeReference,
  type BlendPartialReferenceTarget,
} from '@/core/features/blend/partial-edge';
import {
  removeFaceSetSelection,
  resolveDefaultFilletFaceSetId,
  resolveFaceSetItemRefs,
  upsertFaceSetSelection,
  type ChamferSelectionTarget,
  type FilletFaceSetId,
  type FilletSelectionTarget,
} from '@/core/features/blend/stage-two';
import type { BlendSelectionDisplayItem } from '@/core/features/blend/types';
import {
  type ActiveBlendKind,
  type BlendObject,
  buildChamferDraft,
  buildFilletDraft,
  normalizeErrorMessage,
  resolveBlendUpstreamBodyObjectId,
  resolveChamferItems,
  resolveFilletItems,
  resolvePartialEdgeItemRefs,
} from './blend-feature-helpers';
import { createBlendFeatureActions } from './blend-feature-actions';
import { createBlendSelectionFlowHelpers } from './blend-selection-flow';
import { createBlendSubmitter } from './blend-submit-flow';
const DEFAULT_FILLET_SELECTION_TARGET: FilletSelectionTarget = 'faces';
const DEFAULT_CHAMFER_SELECTION_TARGET: ChamferSelectionTarget = 'faces';

type BlendSelectionCandidate = {
  object: BlendObject | null;
  selection: {
    itemRef: SelectionItemRef;
    displayItem: BlendSelectionDisplayItem;
  };
};

type UseBlendFeatureOptions = {
  viewportRef: ExtrudeViewportRef;
  updateState: () => Promise<void>;
  message: MessageApi;
};

const blendSelectionSlotResolver = new BlendFeatureSelectionSlotResolver();

export function useBlendFeature(options: UseBlendFeatureOptions) {
  const { viewportRef, updateState, message } = options;
  const activeKind = ref<ActiveBlendKind>(null);
  const editingFeatureId = ref<string | null>(null);
  const upstreamBodyObjectId = ref<string | null>(null);

  const filletDefinition = ref<FilletFeatureDefinition>(createDefaultFilletDefinition());
  const chamferDefinition = ref<ChamferFeatureDefinition>(createDefaultChamferDefinition());
  const filletItems = ref<BlendSelectionDisplayItem[]>([]);
  const chamferItems = ref<BlendSelectionDisplayItem[]>([]);
  const filletActiveFaceSetId = ref<FilletFaceSetId | null>(resolveDefaultFilletFaceSetId(filletDefinition.value.type));
  const chamferActiveFaceSetId = ref<'faceSet1' | 'faceSet2'>('faceSet1');
  const filletSelectionTarget = ref<FilletSelectionTarget>(DEFAULT_FILLET_SELECTION_TARGET);
  const chamferSelectionTarget = ref<ChamferSelectionTarget>(DEFAULT_CHAMFER_SELECTION_TARGET);
  const filletPartialReferenceTarget = ref<BlendPartialReferenceTarget | null>(null);
  const chamferPartialReferenceTarget = ref<BlendPartialReferenceTarget | null>(null);
  const filletSelectedItemRef = ref<string | null>(null);
  const filletSelectedVariablePointKey = ref<string | null>(null);
  const chamferSelectedItemRef = ref<string | null>(null);
  const filletCanSubmit = ref(false);
  const chamferCanSubmit = ref(false);
  const filletPreviewError = ref<string | null>(null);
  const chamferPreviewError = ref<string | null>(null);
  const filletPreviewGuides = ref<BlendPreviewGuide[]>([]);
  const chamferPreviewGuides = ref<BlendPreviewGuide[]>([]);
  let selectionModeBeforeBlend: string | null = null;
  let allowOccludedFeatureTopologySelectionBeforeBlend: boolean | null = null;
  let suppressInternalSelectionReplay = false;
  let commitTransitionActive = false;
  let previewVersion = 0;

  const filletUiState = computed(() => ({
    active: activeKind.value === 'fillet',
    editingFeatureId: activeKind.value === 'fillet' ? editingFeatureId.value : null,
    definition: activeKind.value === 'fillet' ? clonePlainData(filletDefinition.value) : null,
    upstreamBodyObjectId: activeKind.value === 'fillet' ? upstreamBodyObjectId.value : null,
    items: [...filletItems.value],
    activeFaceSetId: filletActiveFaceSetId.value,
    selectionTarget: filletSelectionTarget.value,
    partialReferenceTarget: filletPartialReferenceTarget.value,
    selectedItemRef: filletSelectedItemRef.value,
    selectedVariablePointKey: filletSelectedVariablePointKey.value,
    canSubmit: filletCanSubmit.value,
    previewError: filletPreviewError.value,
    previewGuides: [...filletPreviewGuides.value],
  }));

  const chamferUiState = computed(() => ({
    active: activeKind.value === 'chamfer',
    editingFeatureId: activeKind.value === 'chamfer' ? editingFeatureId.value : null,
    definition: activeKind.value === 'chamfer' ? clonePlainData(chamferDefinition.value) : null,
    upstreamBodyObjectId: activeKind.value === 'chamfer' ? upstreamBodyObjectId.value : null,
    items: [...chamferItems.value],
    activeFaceSetId: chamferActiveFaceSetId.value,
    selectionTarget: chamferSelectionTarget.value,
    partialReferenceTarget: chamferPartialReferenceTarget.value,
    selectedItemRef: chamferSelectedItemRef.value,
    canSubmit: chamferCanSubmit.value,
    previewError: chamferPreviewError.value,
    previewGuides: [...chamferPreviewGuides.value],
  }));

  /**
   * 补齐变量圆角端点，并同步当前控制点选择。
   */
  const syncVariableRadiusControlPoints = (): void => {
    if (filletDefinition.value.type !== 'variable') {
      filletSelectedVariablePointKey.value = null;
      return;
    }
    const instanceCount = clampVariableRadiusInstanceCount(
      filletDefinition.value.variableInstanceCount ?? DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
    );
    const edgeItemRefs = resolvePartialEdgeItemRefs(filletDefinition.value.items ?? []);
    const variableMode = filletDefinition.value.variableMode ?? 'symmetric';
    const endpointPoints = ensureVariableRadiusEndpointPoints(
      filletDefinition.value.variablePoints,
      edgeItemRefs,
      instanceCount,
      filletDefinition.value.radius ?? 10,
      filletDefinition.value.radiusExpression ?? null,
    );
    filletDefinition.value = {
      ...filletDefinition.value,
      variableInstanceCount: instanceCount,
      variableMode,
      variableProfile: variableMode === 'asymmetric' ? 'elliptic' : 'circular',
      variableTransition: filletDefinition.value.variableTransition ?? 'smooth',
      variablePoints: variableMode === 'asymmetric'
        ? endpointPoints.map((point) => ({
          ...point,
          radius2: typeof point.radius2 === 'number' && Number.isFinite(point.radius2) && point.radius2 > 0
            ? point.radius2
            : point.radius ?? filletDefinition.value.radius ?? 10,
          expression2: point.expression2 ?? point.expression ?? filletDefinition.value.radiusExpression ?? null,
        }))
        : endpointPoints,
    };
    filletSelectedVariablePointKey.value = resolveSelectedVariableRadiusPointKey(
      filletDefinition.value.variablePoints,
      filletSelectedVariablePointKey.value,
    );
  };

  /**
   * 使当前预览会话失效。
   *
   * 设计说明：
   * 1. 预览请求是异步的，晚到结果必须通过版本号统一判废；
   * 2. 提交开始后如果不先失效旧预览，晚到 preview 结果会在 commit 过程中重新落回场景；
   * 3. 这里统一承担“停掉定时器 + 清空 guide + 递增版本号”的职责，供普通清预览和提交前清预览共用。
   */
  const invalidatePreviewState = (): void => {
    previewVersion += 1;
    viewportRef.value?.clearFeaturePreviewRequests?.();
    filletPreviewGuides.value = [];
    chamferPreviewGuides.value = [];
  };

  /**
   * 请求移除当前预览 mesh。
   *
   * @returns 预览移除 Promise。
   */
  const requestPreviewRemoval = (): Promise<unknown> => {
    const removalResult = viewportRef.value?.setFeaturePreviewMesh?.(null)
      ?? viewportRef.value?.setExtrudePreviewMesh?.(null)
      ?? null;
    return Promise.resolve(removalResult);
  };

  /**
   * 清空当前预览。
   */
  const clearPreview = (): void => {
    invalidatePreviewState();
    void requestPreviewRemoval();
  };

  /**
   * 在提交切换前同步清空预览。
   *
   * 设计说明：
   * 1. commit 过程中如果旧 preview 还留在场景，用户会看到错误叠加，且晚到 preview 结果会继续污染场景；
   * 2. 这里显式等待预览 mesh 的移除完成，再进入提交与整场景重建；
   * 3. 这样可以把“预览生命周期”和“正式提交生命周期”彻底切开，避免提交后卡顿与残留对象。
   */
  const clearPreviewAsync = async (): Promise<void> => {
    invalidatePreviewState();
    await requestPreviewRemoval();
  };

  /**
   * 提交期间冻结当前预览。
   *
   * 设计说明：
   * 1. 提交会正式重跑 Worker 命令，旧的异步预览结果不能再落回场景；
   * 2. 但已经可见的预览体必须保留到正式结果体同步完成，否则用户会看到实体闪回提交前状态；
   * 3. 因此这里只取消定时器并推进版本号，不移除预览 mesh，也不清空当前 guide。
   */
  const freezePreviewForCommit = (): void => {
    previewVersion += 1;
    viewportRef.value?.clearFeaturePreviewRequests?.();
  };

  /**
   * 解析当前命令激活的输入槽位。
   *
   * 设计说明：
   * 1. 视口拾取模式与宿主吸收规则必须共享同一份输入槽位协议；
   * 2. 圆角/倒角当前只是第一批消费者，后续 Draft/Shell/Replace Face 也要沿用这套真相；
   * 3. 因此 web-client 只读取 cad-core 解析后的槽位，不再本地硬编码新的规则分支。
   */
  const resolveActiveSelectionSlot = () => {
    if (activeKind.value === 'fillet') {
      return blendSelectionSlotResolver.resolveFilletSlot({
        definition: filletDefinition.value,
        selectionTarget: filletSelectionTarget.value,
        activeFaceSetId: filletActiveFaceSetId.value,
      });
    }

    if (activeKind.value === 'chamfer') {
      return blendSelectionSlotResolver.resolveChamferSlot({
        definition: chamferDefinition.value,
        selectionTarget: chamferSelectionTarget.value,
        activeFaceSetId: chamferActiveFaceSetId.value,
      });
    }

    return null;
  };

  /**
   * 瑙ｆ瀽褰撳墠鍛戒护鎬佹槸鍚﹀厑璁哥┛閫忓墠鏅潰閫夋嫨琚伄鎸＄殑瀹炰綋鎷撴墤銆?
   *
   * 璁捐璇存槑锛?
   * 1. `selectThroughFaces` 鏄?SolidWorks 鐗瑰緛鎷撴墤閫夋嫨鐨勫簳灞傜瓥鐣ワ紝涓嶅簲鍋滅暀鍦?UI 灞傦紱
   * 2. 鍦嗚/鍊掕鍛戒护闇€瑕佹妸瀹冧笅娌夊埌 cad-core 閫夋嫨绠＄悊鍣紝鍚﹀垯鍙杈逛笌闅愯棌杈逛細缁х画鍏ㄥ眬绔炰簤锛?
   * 3. 鍚庣画 Draft/Shell/Replace Face 涔熷彲浠ョ洿鎺ュ鐢ㄨ繖涓簳灞傚紑鍏炽€?
   *
   * @returns 鏄惁鍏佽绌块€忓墠鏅潰閫夋嫨銆?
   */
  const resolveAllowOccludedFeatureTopologySelection = (): boolean => {
    if (activeKind.value === 'fillet') {
      return filletDefinition.value.selectThroughFaces === true;
    }
    if (activeKind.value === 'chamfer') {
      return chamferDefinition.value.selectThroughFaces === true;
    }
    return false;
  };

  /**
   * 鎭㈠杩涘叆鍦嗚/鍊掕鍛戒护鍓嶇殑瑙嗗彛閫夋嫨鐜銆?
   *
   * 璁捐璇存槑锛?
   * 1. 閫夋嫨妯″紡涓庘€滄槸鍚﹀厑璁哥┛閫忛€夋嫨鈥濆睘浜庡悓涓€缁勫簳灞傜幆澧冪湡鐩革紝蹇呴』鍚屾椂鎭㈠锛?
   * 2. 鎻愪氦鎴栧彇娑堝懡浠ゅ墠鍏堟仮澶嶈繖缁勭幆澧冿紝鍙互閬垮厤缁撴灉浣撻噸寤虹獥鍙ｄ腑浠嶅浜庨珮浠ｇ悊閫夋嫨妯″紡锛?
   * 3. 璇ュ嚱鏁板彧璐熻矗鎭㈠瑙嗗彛鐜锛屼笉娓呯悊鍛戒护瀹夸富鐘舵€侊紝渚夸簬鎻愪氦閾捐矾鍦ㄥ悓姝ュ墠鎻愬墠闄嶅櫔銆?
   */
  const restoreViewportSelectionEnvironment = (): void => {
    if (selectionModeBeforeBlend) {
      viewportRef.value?.setSelectionMode?.(selectionModeBeforeBlend, { preserveSelection: true });
    }
    if (allowOccludedFeatureTopologySelectionBeforeBlend !== null) {
      viewportRef.value?.setAllowOccludedFeatureTopologySelection?.(allowOccludedFeatureTopologySelectionBeforeBlend);
    }
    selectionModeBeforeBlend = null;
    allowOccludedFeatureTopologySelectionBeforeBlend = null;
  };

  /**
   * 同步当前圆角/倒角命令的选择模式。
   *
   * 设计说明：
   * 1. 命令态必须先约束底层拾取，再谈宿主吸收；否则边/面/顶点会继续被默认对象模式串扰；
   * 2. 固定大小圆角需要在 `face-edge / edge` 之间切换，分别对应主项目和纯边非对称；
   * 3. 倒角当前只需要 `edge / face / vertex` 三种模式，仍统一复用同一出口。
   */
  const syncSelectionMode = (): void => {
    if (!activeKind.value) {
      return;
    }
    const activeSlot = resolveActiveSelectionSlot();
    const mode = activeSlot?.selectionMode ?? 'object';
    const allowOccludedSelection = resolveAllowOccludedFeatureTopologySelection();

    const currentMode = viewportRef.value?.getSelectionMode?.() ?? null;
    const currentTopologyMode = viewportRef.value?.getFeatureTopologySelectionMode?.() ?? 'none';
    const expectedTopologyMode = mode === 'object' ? 'none' : mode;
    const currentAllowOccludedSelection = viewportRef.value?.getAllowOccludedFeatureTopologySelection?.() ?? false;
    if (currentAllowOccludedSelection !== allowOccludedSelection) {
      viewportRef.value?.setAllowOccludedFeatureTopologySelection?.(allowOccludedSelection);
    }
    if (currentMode === mode && currentTopologyMode === expectedTopologyMode) {
      return;
    }

    /**
     * setSelectionMode(..., preserveSelection=true) 会在底层重新发布一次当前选择快照。
     * 这次事件属于宿主内部同步，不能再被当成“用户又点了一次同一条边/面”。
     */
    suppressInternalSelectionReplay = true;
    viewportRef.value?.setSelectionMode?.(mode, { preserveSelection: true });
  };

  /**
   * 从 definition 主选择集重算上游实体 ID。
   *
   * 设计说明：预览/提交仍读取 `upstreamBodyObjectId` ref，但必须与 items 真值保持一致，
   * 不能在首次点击候选时提前锁定，否则清空选择后无法切换到另一个实体。
   */
  const syncUpstreamBodyObjectId = (): void => {
    if (!activeKind.value) {
      upstreamBodyObjectId.value = null;
      return;
    }
    upstreamBodyObjectId.value = resolveBlendUpstreamBodyObjectId(
      activeKind.value,
      filletDefinition.value,
      chamferDefinition.value,
    );
  };

  const syncFillet = (): void => {
    syncUpstreamBodyObjectId();
    filletItems.value = mergeDisplayItemsFromSelectionRefs(filletItems.value, resolveFilletItems(filletDefinition.value));
    filletCanSubmit.value = canSubmitFillet(filletDefinition.value, upstreamBodyObjectId.value, filletItems.value);
    syncCommandSelectionHighlight(resolveFilletItems(filletDefinition.value));
  };

  const syncChamfer = (): void => {
    syncUpstreamBodyObjectId();
    chamferItems.value = mergeDisplayItemsFromSelectionRefs(chamferItems.value, resolveChamferItems(chamferDefinition.value));
    chamferCanSubmit.value = canSubmitChamfer(chamferDefinition.value, upstreamBodyObjectId.value, chamferItems.value);
    syncCommandSelectionHighlight(resolveChamferItems(chamferDefinition.value));
  };

  /**
   * 同步命令吸收项的视口选中态。
   *
   * @param items 当前命令吸收的拓扑项。
   */
  const syncCommandSelectionHighlight = (items: SelectionItemRef[]): void => {
    if (!activeKind.value || items.length === 0 || !viewportRef.value?.selectFeatureTopologyItems) {
      return;
    }
    suppressInternalSelectionReplay = true;
    viewportRef.value.selectFeatureTopologyItems(items);
  };

  const resetSession = (restoreFeature: boolean): void => {
    const currentEditingFeatureId = editingFeatureId.value;
    if (restoreFeature && currentEditingFeatureId) {
      viewportRef.value?.closeFeatureEditSession?.(currentEditingFeatureId);
    }
    restoreViewportSelectionEnvironment();
    activeKind.value = null;
    editingFeatureId.value = null;
    upstreamBodyObjectId.value = null;
    filletDefinition.value = createDefaultFilletDefinition();
    chamferDefinition.value = createDefaultChamferDefinition();
    filletItems.value = [];
    chamferItems.value = [];
    filletActiveFaceSetId.value = resolveDefaultFilletFaceSetId(filletDefinition.value.type);
    chamferActiveFaceSetId.value = 'faceSet1';
    filletSelectionTarget.value = DEFAULT_FILLET_SELECTION_TARGET;
    chamferSelectionTarget.value = DEFAULT_CHAMFER_SELECTION_TARGET;
    filletPartialReferenceTarget.value = null;
    chamferPartialReferenceTarget.value = null;
    filletSelectedItemRef.value = null;
    filletSelectedVariablePointKey.value = null;
    chamferSelectedItemRef.value = null;
    filletCanSubmit.value = false;
    chamferCanSubmit.value = false;
    filletPreviewError.value = null;
    chamferPreviewError.value = null;
    suppressInternalSelectionReplay = false;
    clearPreview();
  };

  const {
    getPrimaryBlendObject,
    resolveSelectionCandidates,
    resolveActivePartialReferenceTarget,
    resolvePartialReferencePointCandidates,
    ensureSameBody,
    isSelectionAlreadyAbsorbed,
    isPartialReferencePointAlreadyAbsorbed,
  } = createBlendSelectionFlowHelpers({
    viewportRef,
    activeKind,
    filletDefinition,
    chamferDefinition,
    filletSelectionTarget,
    chamferSelectionTarget,
    filletPartialReferenceTarget,
    chamferPartialReferenceTarget,
    message,
  });

  const runPreview = async (
    kind: Exclude<ActiveBlendKind, null>,
    currentVersion: number,
    force: boolean,
  ): Promise<void> => {
    const draft = kind === 'fillet'
      ? buildFilletDraft({
        definition: filletDefinition.value,
        upstreamBodyObjectId: upstreamBodyObjectId.value,
        editingFeatureId: editingFeatureId.value,
      })
      : buildChamferDraft({
        definition: chamferDefinition.value,
        upstreamBodyObjectId: upstreamBodyObjectId.value,
        editingFeatureId: editingFeatureId.value,
      });

    const previewInput = {
      featureKind: kind,
      featureId: editingFeatureId.value,
      definition: draft.definitionModel as unknown as FeatureDefinition,
      bindings: draft.bindings,
      preferredItemRef: kind === 'chamfer' ? chamferSelectedItemRef.value : null,
    };
    const viewport = viewportRef.value;
    const queuedResult = viewport?.requestFeaturePreview
      ? await viewport.requestFeaturePreview(previewInput, { force })
      : await (async () => {
        const result = await viewport?.executeFeaturePreview?.(previewInput);
        return result
          ? {
            status: 'completed' as const,
            token: currentVersion,
            result,
          }
          : {
            status: 'stale' as const,
            token: currentVersion,
          };
      })();
    if (queuedResult.status === 'stale') {
      return;
    }

    const result = queuedResult.result;
    if (!result || currentVersion !== previewVersion) {
      return;
    }

    await (
      viewportRef.value?.setFeaturePreviewMesh?.(result.meshData, {
        prototype: result.prototype as unknown as FeaturePrototype,
        draftDefinition: result.draftDefinition as unknown as FeatureDefinition,
      })
      ?? viewportRef.value?.setExtrudePreviewMesh(result.meshData, {
        prototype: result.prototype as unknown as FeaturePrototype,
        draftDefinition: result.draftDefinition as unknown as FeatureDefinition,
      })
    );

    if (kind === 'fillet') {
      filletPreviewError.value = null;
      filletPreviewGuides.value = [...(result.presentation?.blendGuides ?? [])];
      syncFillet();
      filletCanSubmit.value = canSubmitFillet(
        filletDefinition.value,
        upstreamBodyObjectId.value,
        filletItems.value,
      );
    } else {
      chamferPreviewError.value = null;
      chamferPreviewGuides.value = [...(result.presentation?.blendGuides ?? [])];
      syncChamfer();
      chamferCanSubmit.value = canSubmitChamfer(
        chamferDefinition.value,
        upstreamBodyObjectId.value,
        chamferItems.value,
      );
    }
  };

  const schedulePreview = (force = false): void => {
    const kind = activeKind.value;
    if (!kind) {
      return;
    }
    if (commitTransitionActive) {
      return;
    }
    const canPreview = kind === 'fillet'
      ? canPreviewFillet(filletDefinition.value, upstreamBodyObjectId.value, filletItems.value)
      : canPreviewChamfer(chamferDefinition.value, upstreamBodyObjectId.value, chamferItems.value);
    if (!canPreview) {
      clearPreview();
      return;
    }

    const currentVersion = ++previewVersion;
    if (kind === 'fillet') {
      filletCanSubmit.value = false;
    } else {
      chamferCanSubmit.value = false;
    }
    void runPreview(kind, currentVersion, force).catch((error) => {
      /**
       * 旧预览请求比新请求更晚失败时，不能反向覆盖当前最新参数状态。
       * 否则用户刚修改半径/模式，界面却仍显示上一轮失败信息，表现为“参数不生效”。
       */
      if (currentVersion !== previewVersion) {
        return;
      }
      clearPreview();
      const errorMessage = normalizeErrorMessage(error, kind === 'fillet' ? '圆角预览失败。' : '倒角预览失败。');
      if (kind === 'fillet') {
        filletPreviewError.value = errorMessage;
        filletCanSubmit.value = false;
      } else {
        chamferPreviewError.value = errorMessage;
        chamferCanSubmit.value = false;
      }
    });
  };

  const startCommand = (kind: Exclude<ActiveBlendKind, null>): void => {
    resetSession(true);
    activeKind.value = kind;
    selectionModeBeforeBlend = viewportRef.value?.getSelectionMode?.() ?? 'object';
    allowOccludedFeatureTopologySelectionBeforeBlend = viewportRef.value?.getAllowOccludedFeatureTopologySelection?.() ?? false;
    if (kind === 'fillet') {
      filletDefinition.value = createDefaultFilletDefinition();
      filletActiveFaceSetId.value = resolveDefaultFilletFaceSetId(filletDefinition.value.type);
      filletSelectionTarget.value = DEFAULT_FILLET_SELECTION_TARGET;
      syncFillet();
      message.info(describeBlendSelectionTarget('fillet', filletDefinition.value.type));
    } else {
      chamferDefinition.value = createDefaultChamferDefinition();
      chamferActiveFaceSetId.value = 'faceSet1';
      chamferSelectionTarget.value = DEFAULT_CHAMFER_SELECTION_TARGET;
      syncChamfer();
      message.info(describeBlendSelectionTarget('chamfer', chamferDefinition.value.type));
    }
    syncSelectionMode();
    const primary = getPrimaryBlendObject();
    if (primary) {
      const selection = buildBlendSelectionFromObject(primary);
      if (selection) {
        void handleSelectionCandidate({
          object: primary,
          selection,
        }, true);
      }
    }
  };

  const startEdit = async (kind: 'fillet' | 'chamfer', featureId: string): Promise<boolean> => {
    try {
      const payload = kind === 'fillet'
        ? await viewportRef.value?.startFilletEditSession?.(featureId) as FilletEditSessionPayload | undefined
        : await viewportRef.value?.startChamferEditSession?.(featureId) as ChamferEditSessionPayload | undefined;
      if (!payload?.session || !payload.definition) {
        return false;
      }

      resetSession(true);
      activeKind.value = kind;
      editingFeatureId.value = featureId;
      selectionModeBeforeBlend = viewportRef.value?.getSelectionMode?.() ?? 'object';
      allowOccludedFeatureTopologySelectionBeforeBlend = viewportRef.value?.getAllowOccludedFeatureTopologySelection?.() ?? false;

      if (kind === 'fillet') {
        filletDefinition.value = clonePlainData(payload.definition as FilletFeatureDefinition);
        filletActiveFaceSetId.value = resolveDefaultFilletFaceSetId(filletDefinition.value.type);
        filletSelectionTarget.value = DEFAULT_FILLET_SELECTION_TARGET;
        filletItems.value = mergeDisplayItemsFromSelectionRefs([], resolveFilletItems(filletDefinition.value));
        filletSelectedItemRef.value = filletItems.value[0]?.persistentRef ?? null;
        syncVariableRadiusControlPoints();
        syncFillet();
      } else {
        chamferDefinition.value = clonePlainData(payload.definition as ChamferFeatureDefinition);
        chamferSelectionTarget.value = DEFAULT_CHAMFER_SELECTION_TARGET;
        chamferItems.value = mergeDisplayItemsFromSelectionRefs([], resolveChamferItems(chamferDefinition.value));
        chamferSelectedItemRef.value = chamferItems.value[0]?.persistentRef ?? null;
        syncChamfer();
      }

      syncSelectionMode();
      schedulePreview(true);
      return true;
    } catch (error) {
      message.error(normalizeErrorMessage(error, '进入圆角或倒角编辑态失败。'));
      return false;
    }
  };

  const submit = createBlendSubmitter({
    activeKind,
    editingFeatureId,
    upstreamBodyObjectId,
    filletDefinition,
    chamferDefinition,
    filletCanSubmit,
    chamferCanSubmit,
    filletPreviewError,
    chamferPreviewError,
    viewportRef,
    updateState,
    message,
    freezePreviewForCommit,
    restoreViewportSelectionEnvironment,
    clearPreviewAsync,
    resetSession,
    setCommitTransitionActive: (active: boolean) => {
      commitTransitionActive = active;
    },
  });

  /**
   * 写入部分边线参考点。
   *
   * @param pointRef 参考点。
   * @param forcePreview 是否强制刷新预览。
   * @returns 是否已消费本次选择。
   */
  const handlePartialReferencePoint = (
    pointRef: BlendPointRef,
    forcePreview = false,
  ): boolean => {
    const activeTarget = resolveActivePartialReferenceTarget();
    if (!activeTarget) {
      return false;
    }
    if (activeTarget.kind === 'fillet') {
      filletDefinition.value = updatePartialEdgeReference(
        filletDefinition.value,
        activeTarget.target,
        pointRef,
      );
      syncFillet();
      schedulePreview(forcePreview);
      return true;
    }
    chamferDefinition.value = updatePartialEdgeReference(
      chamferDefinition.value,
      activeTarget.target,
      pointRef,
    );
    syncChamfer();
    schedulePreview(forcePreview);
    return true;
  };

  const handleSelectionCandidate = async (
    candidate: BlendSelectionCandidate,
    forcePreview = false,
  ): Promise<boolean> => {
    const { object, selection } = candidate;
    if (resolveActivePartialReferenceTarget()) {
      const pointRef = buildBlendPartialReferencePointFromObject(object);
      if (pointRef) {
        handlePartialReferencePoint(pointRef, forcePreview);
      }
      return true;
    }

    const bodyObjectId = selection.itemRef.bodyObjectId;
    if (!bodyObjectId || !ensureSameBody(bodyObjectId) || !activeKind.value) {
      return true;
    }

    if (activeKind.value === 'fillet') {
      if (filletSelectionTarget.value === 'help-point') {
        if (!object) {
          return true;
        }
        const pointRef = buildBlendPointRefFromObject(object);
        if (pointRef) {
          filletDefinition.value = { ...filletDefinition.value, helpPoint: pointRef };
          syncFillet();
          schedulePreview(forcePreview);
        }
        return true;
      }

      const activeSlot = resolveActiveSelectionSlot();
      if (!activeSlot || !blendSelectionSlotResolver.allowsKind(activeSlot, selection.itemRef.kind)) {
        return true;
      }

      if (filletDefinition.value.type === 'face') {
        if (selection.itemRef.kind === 'face') {
          const faceSetId = filletActiveFaceSetId.value ?? resolveDefaultFilletFaceSetId(filletDefinition.value.type);
          if (faceSetId) {
            const exists = resolveFaceSetItemRefs(filletDefinition.value, faceSetId).includes(selection.itemRef.persistentRef);
            filletDefinition.value = {
              ...filletDefinition.value,
              faceSets: exists
                ? removeFaceSetSelection(filletDefinition.value.faceSets, selection.itemRef.persistentRef)
                : upsertFaceSetSelection(filletDefinition.value.faceSets, faceSetId, selection.itemRef),
            };
            filletSelectedItemRef.value = exists ? null : selection.itemRef.persistentRef;
          }
        }
        syncFillet();
        schedulePreview(forcePreview);
        return true;
      }

      const currentItems = filletDefinition.value.items ?? [];
      const exists = currentItems.some((item) => item.persistentRef === selection.itemRef.persistentRef);
      const nextItems = exists
        ? currentItems.filter((item) => item.persistentRef !== selection.itemRef.persistentRef)
        : [...currentItems, selection.itemRef];
      const nextDefinition = normalizeFilletDefinitionForTypeSwitch({
        ...filletDefinition.value,
        items: nextItems,
        partialEdges: filletDefinition.value.type === 'constant'
          ? ensurePartialEdgeParams(
            filletDefinition.value.partialEdges,
            resolvePartialEdgeItemRefs(nextItems),
          )
          : filletDefinition.value.partialEdges,
        variablePoints: filletDefinition.value.type === 'variable'
          ? ensureVariableRadiusEndpointPoints(
            filletDefinition.value.variablePoints,
            nextItems.map((item) => item.persistentRef),
            filletDefinition.value.variableInstanceCount ?? DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
            filletDefinition.value.radius ?? 10,
            filletDefinition.value.radiusExpression ?? null,
          )
          : filletDefinition.value.variablePoints,
      }, filletDefinition.value.type);
      filletDefinition.value = normalizeConstantFilletDefinition(nextDefinition, nextItems);
      filletSelectedItemRef.value = exists ? null : selection.itemRef.persistentRef;
      syncVariableRadiusControlPoints();
      syncFillet();
      syncSelectionMode();
      schedulePreview(forcePreview);
      return true;
    }

    if (chamferSelectionTarget.value === 'help-point') {
      if (!object) {
        return true;
      }
      const pointRef = buildBlendPointRefFromObject(object);
      if (pointRef) {
        chamferDefinition.value = { ...chamferDefinition.value, helpPoint: pointRef };
        syncChamfer();
        schedulePreview(forcePreview);
      }
      return true;
    }

    const activeSlot = resolveActiveSelectionSlot();
    if (!activeSlot || !blendSelectionSlotResolver.allowsKind(activeSlot, selection.itemRef.kind)) {
      return true;
    }

    if (chamferDefinition.value.type === 'face-face') {
      if (selection.itemRef.kind === 'face') {
        const exists = (chamferDefinition.value.faceSets ?? [])
          .find((faceSet) => faceSet.setId === chamferActiveFaceSetId.value)
          ?.items
          ?.some((item) => item.persistentRef === selection.itemRef.persistentRef) ?? false;
        chamferDefinition.value = {
          ...chamferDefinition.value,
          faceSets: exists
            ? removeFaceSetSelection(chamferDefinition.value.faceSets, selection.itemRef.persistentRef)
            : upsertFaceSetSelection(
              chamferDefinition.value.faceSets,
              chamferActiveFaceSetId.value,
              selection.itemRef,
            ),
        };
        chamferSelectedItemRef.value = exists ? null : selection.itemRef.persistentRef;
      }
      syncChamfer();
      schedulePreview(forcePreview);
      return true;
    }

    if (chamferDefinition.value.type === 'vertex') {
      if (selection.itemRef.kind === 'vertex') {
        chamferDefinition.value = {
          ...chamferDefinition.value,
          items: [selection.itemRef],
        };
        chamferItems.value = [labelBlendSelectionItemByTopologyIndex(selection.displayItem)];
        chamferSelectedItemRef.value = selection.itemRef.persistentRef;
        syncChamfer();
        schedulePreview(forcePreview);
      }
      return true;
    }

    if (selection.itemRef.kind !== 'edge' && selection.itemRef.kind !== 'face') {
      return true;
    }

    const currentItems = chamferDefinition.value.items ?? [];
    const exists = currentItems.some((item) => item.persistentRef === selection.itemRef.persistentRef);
    const nextItems = exists
      ? currentItems.filter((item) => item.persistentRef !== selection.itemRef.persistentRef)
      : [...currentItems, selection.itemRef];
    chamferDefinition.value = normalizeOffsetFaceChamferDefinition({
      ...chamferDefinition.value,
      items: nextItems,
      partialEdges: chamferDefinition.value.type === 'offset-face'
        ? ensurePartialEdgeParams(
          chamferDefinition.value.partialEdges,
          resolvePartialEdgeItemRefs(nextItems),
        )
        : [],
    });
    chamferSelectedItemRef.value = exists ? null : selection.itemRef.persistentRef;
    syncChamfer();
    syncSelectionMode();
    schedulePreview(forcePreview);
    return true;
  };

  const handleBlendSelectionChange = async (snapshot?: ViewportSelectionSnapshot): Promise<boolean> => {
    if (!activeKind.value) {
      return false;
    }
    if (commitTransitionActive) {
      return true;
    }

    if (resolveActivePartialReferenceTarget()) {
      const referencePoint = resolvePartialReferencePointCandidates(snapshot)[0] ?? null;
      if (referencePoint) {
        const isReplay = suppressInternalSelectionReplay
          && isPartialReferencePointAlreadyAbsorbed(referencePoint);
        suppressInternalSelectionReplay = false;
        return isReplay ? true : handlePartialReferencePoint(referencePoint);
      }
      suppressInternalSelectionReplay = false;
      return true;
    }

    const nextSelection = resolveSelectionCandidates(snapshot)[0] ?? null;
    if (nextSelection) {
      if (suppressInternalSelectionReplay) {
        suppressInternalSelectionReplay = false;
        if (isSelectionAlreadyAbsorbed(nextSelection.selection.itemRef)) {
          return true;
        }
      }
      return handleSelectionCandidate(nextSelection);
    }

    suppressInternalSelectionReplay = false;
    if (snapshot) {
      return true;
    }
    const primary = getPrimaryBlendObject();
    if (!primary) {
      return true;
    }
    const selection = buildBlendSelectionFromObject(primary);
    return selection ? handleSelectionCandidate({ object: primary, selection }) : true;
  };

  const selectFilletItem = (persistentRef: string): void => {
    filletSelectedItemRef.value = persistentRef;
    syncVariableRadiusControlPoints();
    schedulePreview(true);
  };

  const selectChamferItem = (persistentRef: string): void => {
    chamferSelectedItemRef.value = persistentRef;
    schedulePreview(true);
  };

  const removeFilletItem = (persistentRef: string): void => {
    if (filletDefinition.value.type === 'face') {
      filletDefinition.value = {
        ...filletDefinition.value,
        faceSets: removeFaceSetSelection(filletDefinition.value.faceSets, persistentRef),
      };
    } else {
      filletDefinition.value = {
        ...filletDefinition.value,
        items: (filletDefinition.value.items ?? []).filter((item) => item.persistentRef !== persistentRef),
        variablePoints: (filletDefinition.value.variablePoints ?? []).filter((point) => point.itemRef !== persistentRef),
        partialEdges: (filletDefinition.value.partialEdges ?? []).filter((item) => item.itemRef !== persistentRef),
      };
    }
    if (filletSelectedItemRef.value === persistentRef) {
      filletSelectedItemRef.value = null;
    }
    syncVariableRadiusControlPoints();
    syncFillet();
    syncSelectionMode();
    schedulePreview(true);
  };

  const removeChamferItem = (persistentRef: string): void => {
    chamferDefinition.value = chamferDefinition.value.type === 'face-face'
      ? normalizeChamferDefinitionForFaceFaceMode({
        ...chamferDefinition.value,
        faceSets: removeFaceSetSelection(chamferDefinition.value.faceSets, persistentRef),
      }, chamferDefinition.value.faceFaceMode ?? 'symmetric')
      : {
        ...chamferDefinition.value,
        items: (chamferDefinition.value.items ?? []).filter((item) => item.persistentRef !== persistentRef),
        partialEdges: (chamferDefinition.value.partialEdges ?? []).filter((item) => item.itemRef !== persistentRef),
      };
    if (chamferSelectedItemRef.value === persistentRef) {
      chamferSelectedItemRef.value = null;
    }
    syncChamfer();
    syncSelectionMode();
    schedulePreview(true);
  };

  onUnmounted(() => resetSession(true));

  return createBlendFeatureActions({
    editingFeatureId,
    filletDefinition,
    chamferDefinition,
    filletItems,
    chamferItems,
    filletActiveFaceSetId,
    chamferActiveFaceSetId,
    filletSelectionTarget,
    chamferSelectionTarget,
    filletPartialReferenceTarget,
    chamferPartialReferenceTarget,
    filletSelectedItemRef,
    filletSelectedVariablePointKey,
    chamferSelectedItemRef,
    filletUiState,
    chamferUiState,
    startCommand,
    startEdit,
    handleBlendSelectionChange,
    resetSession,
    submit,
    syncFillet,
    syncChamfer,
    syncVariableRadiusControlPoints,
    syncSelectionMode,
    schedulePreview,
    selectFilletItem,
    removeFilletItem,
    selectChamferItem,
    removeChamferItem,
    message,
  });
}
